import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import StockDetailsModal from '../components/StockDetailsModal';
import FloatingAIAssistant from '../components/FloatingAIAssistant';
import FloatingCompetitionButton from '../components/FloatingCompetitionButton';
import MobileTopBarClean from '../components/MobileTopBarClean';
import { useTranslation } from 'react-i18next';
import '../styles/global-white-backgrounds.css';
import '../styles/professional-investment-cards.css';
import '../styles/stocks-page.css';
import { useIsMobile } from '../hooks/useIsMobile';

const BondETFPage = () => {
  const navigate = useNavigate();
  const { getHeaderStats } = usePortfolioStats();
  const { t } = useTranslation();
  const [bondETFs, setBondETFs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBond, setSelectedBond] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [draggedBond, setDraggedBond] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const dragElementRefs = useState(() => new Map())[0];

  useEffect(() => {
    fetchBondETFs();
  }, []);


  // Setup non-passive touch events for mobile drag
  useEffect(() => {
    if (!isMobile) return;

    const handleTouchMove = (e) => {
      if (!touchStartPos || !draggedBond) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.x);
      const deltaY = Math.abs(currentY - touchStartPos.y);
      
      // Very low threshold for easy mobile dragging
      if (deltaX > 5 || deltaY > 5) {
        if (!touchMoved) {
          setTouchMoved(true);
          setIsDragging(true);
        }
        
        // Update drag position for visual feedback
        const dragX = currentX - touchStartPos.x;
        const dragY = currentY - touchStartPos.y;
        setDragPosition({ x: dragX, y: dragY });
        
        // Check if dragging over drop zone for visual feedback
        const elementAtPoint = document.elementFromPoint(currentX, currentY);
        const overDropZone = elementAtPoint?.closest('.icon-container') || 
                           elementAtPoint?.closest('.floating-ai-assistant');
        setIsOverDropZone(!!overDropZone);
        
        // Add haptic feedback on iOS when over drop zone
        if (overDropZone && navigator.vibrate) {
          navigator.vibrate(10);
        }
        
        e.preventDefault(); // This will now work with non-passive events
        e.stopPropagation();
      }
    };

    // Add non-passive touch event listeners to all draggable elements
    dragElementRefs.forEach((element) => {
      if (element) {
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
      }
    });

    return () => {
      dragElementRefs.forEach((element) => {
        if (element) {
          element.removeEventListener('touchmove', handleTouchMove);
        }
      });
    };
  }, [isMobile, touchStartPos, draggedBond, touchMoved, dragElementRefs]);

  const fetchBondETFs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Top 5 biggest bond ETF tickers by assets under management
      const bondTickers = [
        'AGG',  // iShares Core U.S. Aggregate Bond ETF
        'BND',  // Vanguard Total Bond Market ETF
        'LQD',  // iShares iBoxx $ Investment Grade Corporate Bond ETF
        'TLT',  // iShares 20+ Year Treasury Bond ETF
        'HYG'   // iShares iBoxx $ High Yield Corporate Bond ETF
      ];
      
      const bondETFsWithDetails = await Promise.all(
        bondTickers.map(async (ticker) => {
          try {
            // Get detailed company info
            const detailResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}`);
            const detailData = await detailResponse.json();
            
            // Get current price data 
            const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${ticker}/prev`);
            const priceData = await priceResponse.json();
            
            const companyDetails = detailData.results || {};
            const currentData = priceData.results?.[0] || {};
            
            return {
              ticker: ticker,
              name: companyDetails.name || ticker,
              currentPrice: currentData.c || 0,
              close: currentData.c || 0, // StockDetailsModal expects 'close'
              ask: currentData.h || 0, // Day high as ask
              bid: currentData.l || 0, // Day low as bid
              change: 0, // Will be calculated properly later
              changePercent: 0,
              dayHigh: currentData.h || 0,
              dayLow: currentData.l || 0,
              volume: currentData.v || 0,
              marketCap: companyDetails.market_cap || 0,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${ticker}/icon`
            };
          } catch (error) {
            console.warn(`Failed to enhance data for ${ticker}:`, error);
            return null;
          }
        })
      );
      
      // Filter out failed requests
      const validBondETFs = bondETFsWithDetails.filter(bond => bond);
      
      // If we don't have data for the specific tickers, try to find bond ETFs through search
      if (validBondETFs.length === 0) {
        console.warn('No bond ETF data found for specific tickers, trying search...');
        
        // Fallback: Try to get any bond-related ETFs
        const fallbackResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers?market=stocks&type=ETF&active=true&limit=100`);
        const fallbackData = await fallbackResponse.json();
        
        if (fallbackData.status === 'OK' && fallbackData.results) {
          // Filter for bond-related ETFs based on name
          const bondKeywords = ['bond', 'treasury', 'corporate', 'aggregate', 'fixed', 'income'];
          const potentialBondETFs = fallbackData.results.filter(etf => 
            bondKeywords.some(keyword => 
              etf.name?.toLowerCase().includes(keyword) || 
              etf.ticker?.toLowerCase().includes(keyword)
            )
          ).slice(0, 5); // Take only first 5
          
          const fallbackETFs = await Promise.all(
            potentialBondETFs.map(async (etf) => {
              try {
                const priceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/tickers/${etf.ticker}/prev`);
                const priceData = await priceResponse.json();
                const currentData = priceData.results?.[0] || {};
                
                return {
                  ticker: etf.ticker,
                  name: etf.name || etf.ticker,
                  currentPrice: currentData.c || 0,
                  close: currentData.c || 0,
                  ask: currentData.h || 0,
                  bid: currentData.l || 0,
                  change: 0,
                  changePercent: 0,
                  dayHigh: currentData.h || 0,
                  dayLow: currentData.l || 0,
                  volume: currentData.v || 0,
                  marketCap: 0,
                  logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/logo`,
                  iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${etf.ticker}/icon`
                };
              } catch (error) {
                console.warn(`Failed to fetch fallback data for ${etf.ticker}:`, error);
                return null;
              }
            })
          );
          
          setBondETFs(fallbackETFs.filter(bond => bond));
        } else {
          // If all else fails, create mock data for the top 5 bond ETFs
          setBondETFs([
            {
              ticker: 'AGG',
              name: 'iShares Core U.S. Aggregate Bond ETF',
              currentPrice: 102.85,
              close: 102.85,
              ask: 102.90,
              bid: 102.80,
              change: 0.15,
              changePercent: 0.15,
              dayHigh: 103.05,
              dayLow: 102.70,
              volume: 4250000,
              marketCap: 95000000000,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/AGG/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/AGG/icon`
            },
            {
              ticker: 'BND',
              name: 'Vanguard Total Bond Market ETF',
              currentPrice: 76.45,
              close: 76.45,
              ask: 76.50,
              bid: 76.40,
              change: 0.08,
              changePercent: 0.10,
              dayHigh: 76.65,
              dayLow: 76.35,
              volume: 5100000,
              marketCap: 280000000000,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/BND/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/BND/icon`
            },
            {
              ticker: 'LQD',
              name: 'iShares iBoxx $ Investment Grade Corporate Bond ETF',
              currentPrice: 109.25,
              close: 109.25,
              ask: 109.30,
              bid: 109.20,
              change: 0.22,
              changePercent: 0.20,
              dayHigh: 109.55,
              dayLow: 109.10,
              volume: 12500000,
              marketCap: 35000000000,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/LQD/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/LQD/icon`
            },
            {
              ticker: 'TLT',
              name: 'iShares 20+ Year Treasury Bond ETF',
              currentPrice: 92.15,
              close: 92.15,
              ask: 92.20,
              bid: 92.10,
              change: -0.35,
              changePercent: -0.38,
              dayHigh: 92.65,
              dayLow: 91.95,
              volume: 18500000,
              marketCap: 22000000000,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/TLT/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/TLT/icon`
            },
            {
              ticker: 'HYG',
              name: 'iShares iBoxx $ High Yield Corporate Bond ETF',
              currentPrice: 78.95,
              close: 78.95,
              ask: 79.00,
              bid: 78.90,
              change: 0.12,
              changePercent: 0.15,
              dayHigh: 79.15,
              dayLow: 78.75,
              volume: 15200000,
              marketCap: 14500000000,
              logoUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/HYG/logo`,
              iconUrl: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/HYG/icon`
            }
          ]);
        }
      } else {
        setBondETFs(validBondETFs);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error fetching bond ETFs:', error);
      setError('Failed to load bond ETF data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle bond ETF click - open stock details modal
  const handleBondClick = (bond) => {
    setSelectedBond(bond);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBond(null);
  };

  return (
    <div className="dashboard-container jpx-style">
      <div className="main-content" style={{ marginLeft: 0, width: '100%' }}>
        {/* X BUTTON TO RETURN TO DASHBOARD */}
        <button className="modal-close" onClick={() => navigate('/dashboard')} style={{
          position: 'fixed',
          top: '120px',
          right: '20px',
          width: '40px',
          height: '40px',
          background: '#000000',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '24px',
          cursor: 'pointer',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        }}>×</button>
        {/* Conditional Header - Mobile vs Desktop */}
        <MobileTopBarClean stats={getHeaderStats()} />


        {/* Main content */}
        <main className="dashboard-content" style={{
          marginTop: isMobile ? '75px' : '0'
        }}>
          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon"></span>
              {error}
            </div>
          )}

          {/* Bond ETF Grid */}
          <section className="stocks-section">
            <div className="section-header">
              <h2 className="section-title">{t('assets:pages.bondEtf.sectionTitle')}</h2>
              <div className="section-subtitle">{t('assets:pages.bondEtf.sectionSubtitle')}</div>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>{t('assets:loading.bondEtfData')}</p>
              </div>
            ) : (
              <div className="stocks-grid">
                {bondETFs.map((bond) => (
                  <div 
                    key={bond.ticker}
                    className="stock-card stocks"
                    onClick={(e) => {
                      // Don't trigger click if we're dragging
                      if (!touchMoved && !draggedBond) {
                        handleBondClick(bond);
                      }
                    }}
                  >
                    {/* Bond Logo - Draggable */}
                    <div 
                      ref={(el) => {
                        if (el) {
                          dragElementRefs.set(bond.ticker, el);
                        }
                      }}
                      className={`stock-logo draggable-logo ${
                        draggedBond === bond.ticker ? 'dragging-active' : ''
                      } ${isDragging && draggedBond === bond.ticker ? 'dragging-move' : ''}`}
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          ticker: bond.ticker,
                          name: bond.name,
                          price: bond.close,
                          type: 'bond-etf'
                        }));
                        e.dataTransfer.effectAllowed = 'copy';
                        setDraggedBond(bond.ticker);
                      }}
                      onDragEnd={(e) => {
                        setDraggedBond(null);
                      }}
                      // Mobile touch support
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        const touch = e.touches[0];
                        setTouchStartPos({
                          x: touch.clientX,
                          y: touch.clientY,
                          time: Date.now(),
                          pageX: touch.pageX,
                          pageY: touch.pageY
                        });
                        setTouchMoved(false);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        
                        // Provide immediate visual feedback
                        setDraggedBond(bond.ticker);
                      }}
                      onTouchMove={(e) => {
                        // This is now handled by the non-passive event listener in useEffect
                        if (touchStartPos && draggedBond === bond.ticker) {
                          e.stopPropagation();
                        }
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        
                        if (!touchStartPos) {
                          return;
                        }
                        
                        // Only trigger if user dragged to a drop zone
                        if (touchMoved) {
                          // Check if touch ended over the AI assistant drop zone
                          const touchEndX = e.changedTouches[0].clientX;
                          const touchEndY = e.changedTouches[0].clientY;
                          const elementAtEnd = document.elementFromPoint(touchEndX, touchEndY);
                          
                          // Look for the AI assistant drop zone or its children
                          const dropZone = elementAtEnd?.closest('.icon-container') || 
                                          elementAtEnd?.closest('.floating-ai-assistant');
                          
                          if (dropZone) {
                            console.log('✅ MOBILE DRAG: Dispatching event for', bond.ticker);
                            const event = new CustomEvent('tickerDropped', {
                              detail: {
                                ticker: bond.ticker,
                                name: bond.name,
                                price: bond.close,
                                type: 'bond-etf'
                              }
                            });
                            window.dispatchEvent(event);
                            console.log('✅ MOBILE DRAG: Event dispatched successfully');
                          } else {
                            console.log('❌ MOBILE DRAG: Not over drop zone. Element:', elementAtEnd?.className);
                          }
                        }
                        
                        // Reset all touch state
                        setTouchStartPos(null);
                        setTouchMoved(false);
                        setDraggedBond(null);
                        setIsDragging(false);
                        setDragPosition({ x: 0, y: 0 });
                        setIsOverDropZone(false);
                      }}
                      style={{ 
                        cursor: 'grab',
                        opacity: draggedBond === bond.ticker ? (isDragging ? 0.8 : 0.9) : 1,
                        '--drag-x': isDragging && draggedBond === bond.ticker ? `${dragPosition.x}px` : '0px',
                        '--drag-y': isDragging && draggedBond === bond.ticker ? `${dragPosition.y}px` : '0px',
                        '--drag-scale': draggedBond === bond.ticker ? 
                          (isDragging ? (isOverDropZone ? '1.25' : '1.15') : '1.05') : '1',
                        '--drag-rotate': isDragging && draggedBond === bond.ticker ? '2deg' : '0deg',
                        boxShadow: draggedBond === bond.ticker ? '0 8px 20px rgba(0,0,0,0.3)' : 'none',
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        touchAction: 'none',
                        WebkitUserDrag: 'element',
                        userSelect: 'none',
                        backgroundColor: draggedBond === bond.ticker ? 
                          (isOverDropZone ? '#e0f2fe' : '#f0f0f0') : 'transparent',
                        borderRadius: '8px',
                        zIndex: draggedBond === bond.ticker ? 1000 : 1
                      }}
                      title={isMobile ? t('assets:card.mobileDragHintBond') : t('assets:card.dragHintBond')}
                    >
                      <img 
                        src={bond.logoUrl}
                        alt={`${bond.name} logo`}
                        style={{ 
                          pointerEvents: 'none',
                          touchAction: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          WebkitTouchCallout: 'none'
                        }}
                        onError={(e) => {
                          // First fallback: try icon instead of logo
                          if (e.target.src === bond.logoUrl) {
                            e.target.src = bond.iconUrl;
                          } else {
                            // Final fallback: placeholder with bond-themed colors
                            const bgColor = '%23003d6b'; // Dark blue for bonds (stability)
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="' + bgColor + '" rx="16"/><text x="50" y="60" font-family="Arial, sans-serif" font-size="36" font-weight="bold" fill="white" text-anchor="middle">' + bond.ticker[0] + '</text></svg>';
                          }
                        }}
                      />
                    </div>

                    {/* Bond Info */}
                    <div className="stock-info">
                      <div className="stock-header">
                        <h3 className="stock-ticker">{bond.ticker}</h3>
                        <div className="stock-name">{bond.name}</div>
                      </div>

                      <div className="stock-price">
                        <span className="current-price">${bond.currentPrice?.toFixed(2) || 'N/A'}</span>
                        <div className={`price-change ${bond.change >= 0 ? 'positive' : 'negative'}`}>
                          {bond.change >= 0 ? '+' : ''}{bond.change?.toFixed(2) || 'N/A'} 
                          ({bond.changePercent >= 0 ? '+' : ''}{bond.changePercent?.toFixed(2) || 'N/A'}%)
                        </div>
                      </div>

                      <div className="stock-metrics">
                        <div className="metric">
                          <span className="metric-label">Day High</span>
                          <span className="metric-value">${bond.dayHigh?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Day Low</span>
                          <span className="metric-value">${bond.dayLow?.toFixed(2) || 'N/A'}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Volume</span>
                          <span className="metric-value">{bond.volume?.toLocaleString() || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      className="stock-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBondClick(bond);
                      }}
                    >
                      {t('assets:card.actions.viewDetails')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!loading && bondETFs.length === 0 && !error && (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>{t('assets:errors.noBondEtfsFound')}</h3>
                <p>{t('assets:errors.bondDataSoon')}</p>
              </div>
            )}
          </section>
        </main>

        {/* Stock Details Modal */}
        {modalOpen && selectedBond && (
          <StockDetailsModal 
            stock={selectedBond}
            isOpen={modalOpen}
            onClose={handleCloseModal}
          />
        )}

        {/* Floating AI Assistant */}
        <FloatingAIAssistant />

        {/* Floating Competition Button */}
        <FloatingCompetitionButton />
      </div>
    </div>
  );
};

export default BondETFPage;